import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { Card, IconButton, PixelPlaceholderLoader, ProgressBar } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";
import styled from "styled-components";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { CHART_COLORS } from "app/constants/ChartColors";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import type {
    IDeviceTotalVisitsResponseData,
    ITrafficAndEngagementWidgetProps,
} from "app/pages/website-analysis/TrafficAndEngagement/types";

import { CompareCardHeader } from "../common/CompareCardHeader";
import { DOMAIN_COLUMN_MIN_WIDTH, NOT_AVAILABLE_VALUE } from "../common/constants";
import { DomainWithTooltipLink } from "../common/DomainWithTooltipLink";
import { EmptyStatePlaceholder } from "../common/EmptyStatePlaceholder";
import { useSubtitleFilterParams } from "../common/useSubtitleFilterParams";

const TABLE_METRIC_NAME = "EngagementVisits";
const VALUE_COLUMN_WIDTH = "70px";

const formatPercent = percentageSignFilter();
const formatAbsolute = minVisitsAbbrFilter();

const StyledIconButton = styled(IconButton)<{ $isActive?: boolean }>`
    ${({ $isActive }) =>
        $isActive &&
        `
        background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
        svg {
            color: ${colorsPalettes.carbon[300]};
        }
    `}
`;

type DisplayMode = "absolute" | "percentage";

const DISPLAY_MODE_ITEMS = [
    { mode: "percentage" as DisplayMode, iconName: "percent" as const },
    { mode: "absolute" as DisplayMode, iconName: "abs-value" as const },
];

export const TotalVisits = ({
    subtitleFilters,
    data,
    hasGA,
    isLoading,
    params,
    showA2d = true,
}: ITrafficAndEngagementWidgetProps) => {
    const { translate } = useTranslate();
    const [displayMode, setDisplayMode] = useState<DisplayMode>("absolute");

    const { countryCode, countryText, dateRangeText } = useSubtitleFilterParams(subtitleFilters);

    const safeParams = (params ?? {}) as { webSource?: IWidgetModelTypesWebSource };

    const typedData = data as IDeviceTotalVisitsResponseData | undefined;

    const rows = useMemo(() => {
        const rawData = typedData?.Data ?? [];

        return rawData.map((item, index) => ({
            domain: item.Domain,
            favicon: item.Favicon,
            shareOfVisits: item.ShareOfVisits,
            totalVisits: item.TotalVisits,
            color: CHART_COLORS.compareMainColors[index % CHART_COLORS.compareMainColors.length],
        }));
    }, [typedData]);

    const onModeClick = useCallback((mode: DisplayMode) => {
        setDisplayMode(mode);
    }, []);

    const modeSwitcher = (
        <Flex alignItems="center">
            {DISPLAY_MODE_ITEMS.map((item) => (
                <StyledIconButton
                    key={item.mode}
                    iconSize="xs"
                    iconName={item.iconName}
                    type="flat"
                    $isActive={displayMode === item.mode}
                    onClick={() => onModeClick(item.mode)}
                    data-automation={`total-visits-toggle-${item.mode}`}
                />
            ))}
        </Flex>
    );

    const isEmpty = rows.length === 0;

    if (isLoading) {
        return (
            <Card data-automation="total-visits-compare-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                    data-automation="total-visits-loading"
                >
                    <PixelPlaceholderLoader
                        width="40%"
                        height={16}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                </Flex>
            </Card>
        );
    }

    if (isEmpty) {
        return (
            <Card data-automation="total-visits-compare-widget">
                <CompareCardHeader
                    title={translate("wa.ao.totalvisits")}
                    tooltipText={translate("wa.ao.totalvisits.tooltip")}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                    dataAutomation="compare-total-visits"
                />
                <EmptyStatePlaceholder />
            </Card>
        );
    }

    return (
        <Card data-automation="total-visits-compare-widget">
            <CompareCardHeader
                title={translate("wa.ao.totalvisits")}
                tooltipText={translate("wa.ao.totalvisits.tooltip")}
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
                dataAutomation="compare-total-visits"
                headerAction={
                    showA2d ? (
                        <AddToDashboard
                            webSource={safeParams.webSource}
                            type="Table"
                            metric={TABLE_METRIC_NAME}
                            modelType="fromWebsite"
                            filters={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                ShouldGetVerifiedData: hasGA,
                            }}
                        />
                    ) : null
                }
            />
            <Flex
                flexDirection="column"
                gap="16px"
                padding="16px 24px"
                flex="1"
                data-automation="total-visits-table"
            >
                <Flex
                    alignItems="center"
                    gap="12px"
                    data-automation="total-visits-table-header"
                >
                    <Flex
                        flex="2"
                        minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                    >
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("global.domain")}
                        </Text>
                    </Flex>
                    <Flex
                        flex="3"
                        minWidth="0"
                        alignItems="center"
                        gap="12px"
                        justifyContent="flex-end"
                    >
                        <Flex flex="0 0 auto">{modeSwitcher}</Flex>
                    </Flex>
                </Flex>
                {rows.map((row) => {
                    const valueText =
                        displayMode === "percentage"
                            ? String(formatPercent(row.shareOfVisits, 1) ?? NOT_AVAILABLE_VALUE)
                            : String(formatAbsolute(row.totalVisits) ?? NOT_AVAILABLE_VALUE);

                    return (
                        <Flex
                            key={row.domain}
                            alignItems="center"
                            gap="12px"
                            data-automation="total-visits-row"
                        >
                            <Flex
                                flex="2"
                                minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                                alignItems="center"
                            >
                                <DomainWithTooltipLink
                                    domain={row.domain}
                                    favicon={row.favicon ?? ""}
                                />
                            </Flex>
                            <Flex
                                flex="3"
                                minWidth="0"
                                alignItems="center"
                                gap="12px"
                            >
                                <Flex
                                    flex="1"
                                    minWidth="0"
                                >
                                    <ProgressBar
                                        values={[row.shareOfVisits]}
                                        colorSet={[row.color]}
                                    />
                                </Flex>
                                <Flex
                                    width={VALUE_COLUMN_WIDTH}
                                    maxWidth={VALUE_COLUMN_WIDTH}
                                    justifyContent="flex-end"
                                >
                                    <Text
                                        fontVariant="input2"
                                        color={colorsPalettes.carbon[400]}
                                        textAlign="right"
                                        data-automation="total-visits-value"
                                    >
                                        {valueText}
                                    </Text>
                                </Flex>
                            </Flex>
                        </Flex>
                    );
                })}
            </Flex>
        </Card>
    );
};
